import { useEffect, useState } from 'react'
import { crud } from '@think-internet/zeus-frontend-package'
import { SurveyContext } from '../../types/Survey/Survey'
import props from '../../redux/props'
import _ from 'lodash'

export type Update = (data: SurveyContext) => void
export type Save = () => Promise<boolean>

const useLoadSurveyContext = (uuid: string): [SurveyContext, Update, Save, boolean] => {
  const surveyContextFeature = crud.use(props.SURVEY_CONTEXT)
  const [context, setContext] = useState<SurveyContext>(null)
  const [savedState, setSavedState] = useState<SurveyContext>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)

  useEffect(() => {
    const get = async () => {
      const data = await surveyContextFeature.get({ uuid })

      if (data) {
        setContext(data)
        setSavedState(data)
      }
    }
    if (!!uuid && (!context || context.uuid !== uuid)) get()
  }, [uuid, context, surveyContextFeature])

  useEffect(() => {
    if (!!context && !!savedState) {
      setHasUnsavedChanges(!_.isEqual(context, savedState))
    }
  }, [context, savedState])

  useEffect(() => {
    const onBeforeUnload = (ev) => {
      if (hasUnsavedChanges) ev.returnValue = true
    }
    window.addEventListener('beforeunload', onBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnsavedChanges])

  const update = (data: SurveyContext) => {
    setContext(data)
  }

  const save = async () => {
    const updatedSurveyContext = await surveyContextFeature.upsert(context)
    if (updatedSurveyContext) {
      setContext(updatedSurveyContext)
      setSavedState(updatedSurveyContext)
    }
    return !!updatedSurveyContext
  }

  return !!context && context.uuid === uuid ? [context, update, save, hasUnsavedChanges] : [null, null, null, false]
}

export default useLoadSurveyContext
